import { AfterContentChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IObject, IValueObject } from '../../../../../type_definitions/datatypes';
import { IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { GaValidateService } from '../../../../shared/formly/ga-validate.service';
import { GaMessageService } from '../../../../shared/message/ga-message.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import * as _ from 'underscore';
import { IDashboardExportMetadata, IDashboardMetadata } from '../dashboard-metadata.service';
import { IDashboardSection } from '../dashboard.component';
import { util } from '../../../../shared/util';
import { IColumnMetaData, IReportMetadata, ReportDataService } from '../../../../shared/report/report-data.service';
import { ReportFiltersUtil } from '../../../../shared/report/report-filters-util.service';
import { DashboardService } from '../dashboard.service';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { IUserData } from '../../../../shared/services/auth/auth-data.service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { GaEventService, IGAEvent } from '../../../../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { EVENT_EXPORT_REPORT } from '../../../../shared/app-events';

export type IDashboardValidatorFn = (doNotWarn?:boolean) => Promise<void | string[]>;

@Component({
  selector: 'base-dashboard-filters',
  templateUrl: './base-dashboard-filters.component.html'
})
export class BaseDashboardFiltersComponent implements OnInit, OnChanges, OnDestroy, AfterContentChecked {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:IObject = {};
  public fields:IExtraFieldConfigurationObject[] = [];
  public exporting:boolean;
  public displayFilters:boolean = true; // displayFilters is for displaying or not at all the filters section
  public isFiltersSectionExpanded:boolean = true; // isFiltersSectionExpanded keeps the status of expanded/collapsed filters
  public searchFiltersText:string = this.translationService.instant('GA.GENERAL.SEARCH_LABEL', { label: 'GA.GENERAL.FILTERS' });
  public autoSearch:boolean;

  @Input() public dashboardMetadata:IDashboardMetadata;
  @Input() public filters:IObject = {};
  @Input() public sections:IDashboardSection[] = [];
  @Input() public dashboardCode:string;
  @Input() public companyCountryId:string;
  @Input() public countryId:number;
  @Input() public allowSearchWithoutFilters:boolean;
  @Input() public loading:boolean;
  @Input() public showFiltersParameter:boolean; // showFiltersParameter is for getting 'showfilter' url parameter (ui-router)

  @Output() public onSearch:EventEmitter<IObject> = new EventEmitter<IObject>();
  @Output() public onFilterChange:EventEmitter<IObject> = new EventEmitter<IObject>();
  @Output() public onValidatorRegister:EventEmitter<IDashboardValidatorFn> = new EventEmitter<IDashboardValidatorFn>();

  protected currentUser:IUserData;
  protected isACSLUser:boolean;
  protected customValidatorFn:IDashboardValidatorFn;
  protected wrapperClass:string = 'col-md-6';
  private eventHandler:Subscription;

  constructor(
    protected validateService:GaValidateService,
    protected messageService:GaMessageService,
    protected translationService:TranslationService,
    protected reportDataService:ReportDataService,
    protected reportFiltersUtil:ReportFiltersUtil,
    protected dashboardService:DashboardService,
    protected deferredService:GaDeferredService,
    protected authService:AuthService,
    protected cdr:ChangeDetectorRef,
    protected eventService?:GaEventService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.currentUser = this.authService.getCurrentUser();
    this.isACSLUser = this.authService.checkAcslUser();

    if (this.displayFilters) {
      this.model = this.filters;
      this.isFiltersSectionExpanded = this.showFiltersParameter;
      this.fields = this.createFields();
      this.onValidatorRegister.emit(this.validateFields);
      this.fillFields();
      this.listen();
    }

    // emit that filters component has been initialized
    this.dashboardService.emitEventFiltersInit();

    if (this.autoSearch) {
      this.startSearch();
    }
  }
  
  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.dashboardCode) {
      this.dashboardCode = changes.dashboardCode.currentValue;
    }

    if (changes.dashboardMetadata) {
      this.dashboardMetadata = changes.dashboardMetadata.currentValue;
    }

    if (changes.filters) {
      this.filters = _.clone(changes.filters.currentValue);

      if(this.fields) {
        this.setQueryFilters();
      }
    }

    if (this.displayFilters && changes.showFiltersParameter) {
      this.showFiltersParameter = changes.showFiltersParameter.currentValue;
      this.isFiltersSectionExpanded = this.showFiltersParameter;
    }
  }

  public ngAfterContentChecked():void {
    this.cdr.detectChanges();
  }

  protected listen():void {
    this.form.valueChanges.subscribe((model: IObject) => {
      this.onFilterChange.emit(model);
    });

    // receive events to start exporting the report
    if(this.eventService) {
      this.eventHandler = this.eventService.events
        .subscribe((event:IGAEvent) => {
          if (event.name === EVENT_EXPORT_REPORT && event?.data) {
            this.doExport(event.data);
          }
        });
    }
  }

  protected setQueryFilters = ():void => {
    if (this.filters) {
      this.dashboardService.applyQueryFiltersToModel(this.filters, this.model, this.fields);
      this.onFilterChange.emit(this.model);
    }

    this.setDashboardFilters();
  };

  protected setDashboardFilters():void {
    const fixedFilters:IObject = this.dashboardService.filtersToDashboardFilters(this.dashboardMetadata?.fixedFilters);
    this.filters =  _.extend(fixedFilters || {}, this.filters);
  }

  public startSearch(doNotWarn?:boolean):void {
    this.validateFields(doNotWarn)
      .then(() => {
        const filters:IObject = this.getFilterModel();
        this.onSearch.emit(filters);
      })
      .catch(util.noop);
  }

  public validateFields:IDashboardValidatorFn = (doNotWarn?:boolean):Promise<void|string[]> => {
    if (this.allowSearchWithoutFilters) {
      return Promise.resolve();
    }

    const deferred:IGADeferred<void|string[]> = this.deferredService.defer();
    const showErrors = (errors:string[]):void => {
      // expand filter when validation fails
      this.isFiltersSectionExpanded = true;

      const validationFailedTitle:string = this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED');

      if ((_.isArray(errors) && errors.length === 1) || _.isString(errors)) {
        // we need to do this because promise rejection expect array
        errors = _.isString(errors) ? [errors] : errors;
        this.messageService.showErrorDialog(validationFailedTitle, errors[0]);
      } else {
        this.messageService.showInvalidFormMessage(validationFailedTitle, undefined, errors);
      }

      deferred.reject(errors);
    };

    if (this.customValidatorFn) {
      this.customValidatorFn(doNotWarn)
        .then(deferred.resolve)
        .catch(showErrors);
    }
    else {
      this.validateService.validateFields(this.fields, this.model)
        .then(deferred.resolve)
        .catch(showErrors);
    }

    return deferred.promise;
  };

  public getFilterModel():IObject {
    const filters:IObject = _.clone(this.model);

    _.each(filters, (filterValue:any, filterKey:string) => {
      // when value is array then make it comma separated
      filters[ filterKey ] = _.isArray(filterValue)? filterValue.toString() : filterValue;
    });

    return filters;
  }
  
  public doExport(report:IDashboardExportMetadata, event?:Event):void {
    event?.preventDefault();

    if (!report) { return; }
    
    this.validateFields()
      .then(() => {
        const dashboardFilters:IObject = this.getFilterModel();
        let reportFilters:any = {};

        // apply mapping from dashboard level
        _.each(this.dashboardMetadata.mapping, (dashboardKey:string, reportKey:string) => {
          reportFilters[ reportKey ] = util.getValue(dashboardFilters, dashboardKey);
        });

        // apply mapping from the report export level
        _.each(report.mapping, (exportReportKey:string, reportKey:string) => {
          reportFilters[ reportKey ] = util.getValue(dashboardFilters, exportReportKey);
        });

        this.exporting = true;
        this.reportDataService.loadMetaData(report.id)
          .then((metadata:IReportMetadata) => {
            reportFilters = this.reportFiltersUtil.getQueryFiltersFromModel(reportFilters, metadata, {
              applyDefaultValues: true,
              applyNullValues: true
            });

            this.getReportTemplateId(report)
              .then((templateId:number) => {
                this.reportDataService.getExcel(
                  report.id,
                  report.filters? util.copyAndExtend(reportFilters, report.filters) : reportFilters,
                  undefined,
                  templateId,
                  metadata.columns.filter((col:IColumnMetaData) => col.formatting && col.formatting.defaultinexport && col.formatting.showinexport)
                )
                  .catch(util.noop)
                  .finally(() => this.exporting = false);
              })
              .catch(util.noop);
          });
      })
      .catch(util.noop);
  }
  
  public createFields():IExtraFieldConfigurationObject[] {
    return [];
  }

  public fillFields():void {
    return;
  }

  protected getReportTemplateId(report:IDashboardExportMetadata):Promise<number> {
    return this.reportDataService.loadMetaData(report.id)
      .then((metadata:IReportMetadata) => {
        if (metadata.templates?.length) {
          if (metadata.templates.length === 1) {
            return metadata.templates[0]?.id;
          } else if (report.templateCode) {
            const matchedTemplate:IValueObject = _.findWhere(metadata.templates, { code: report.templateCode });
            return matchedTemplate?.id;
          }
        }
      });
  }

  public toggleFiltersSection(collapsed:boolean):void {
    this.isFiltersSectionExpanded = !collapsed;
  }

  public ngOnDestroy():void {
    this.eventHandler?.unsubscribe();
  }
}